import React from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router";
import C from "classnames";

import { LargePathSvg } from "./LargePathSvg";
import { SmallPathSvg } from "./SmallPathSvg";

import { ContentCardProps, AdminPathCardProps } from "../contentCardTypes";
import { LessonCard } from "../LessonCard/LessonCard";

import stackSvg from "../../../assets/icons/stack.svg";
import clockSvg from "../../../assets/icons/clock.svg";

import { buildRoute } from "../../../../constants/routes";
import { isEmpty } from "../../../../utils";

import S from "./PathCard.module.scss";
import { formatDuration } from "../../../utils";

export const PathCard: React.FC<ContentCardProps | AdminPathCardProps> = props => {
  const isAdminProps = (
    props: ContentCardProps | AdminPathCardProps
  ): props is AdminPathCardProps => {
    return "onActivate" in props || "onClone" in props;
  };

  const {
    contentType = "path",
    points = 1000,
    variant = "mauve",
    id,
    collectionKey,
    name,
    description,
    lessonsPassed,
    lessonsInEnrollment,
    estimatedTimeToComplete,
    cardSize = "large",
    nextLesson,
    showActivated = false,
    activated = false,
    contentTypeTag,
    disabled = false,
    pathType = "Path",
    // Admin-specific props (only available if isAdminProps is true)
    onActivate,
    onClone,
    isLoadingToggle,
  } = props as AdminPathCardProps;
  const navigate = useNavigate();

  const navigateToPath = (): void => {
    isAdminProps(props)
      ? navigate(buildRoute("Admin.routes.paths.routes.detail.route", { id: id }))
      : pathType === "Path"
        ? navigate(buildRoute("Learn.routes.path.route", { pathId: collectionKey || id }))
        : navigate(buildRoute("Learn.routes.quest.route", { pathId: collectionKey || id }));
  };

  const buttonText = lessonsPassed ? `Continue ${pathType}` : `View ${pathType}`;
  const isLargeCard = cardSize === "large";
  const showLessonCard =
    isLargeCard && lessonsPassed !== lessonsInEnrollment && nextLesson && !isEmpty(nextLesson);
  const cardSizeClass = isLargeCard ? S.largeCard : S.smallCard;
  const variantClass = variant ? S[variant] : undefined;
  const isAdmin = isAdminProps(props);
  const showDescription =
    description && !isAdmin && (isLargeCard || (!estimatedTimeToComplete && !lessonsInEnrollment));

  return (
    <div className={C(S.pathCardWrapper, cardSizeClass, variantClass, "group")}>
      <div className={C(S.pathCardContainer, cardSizeClass, variantClass)}>
        {cardSize === "large" && <LargePathSvg className={S.largeLessonSvg} />}
        {cardSize === "small" && <SmallPathSvg className={S.smallLessonSvg} />}
        <div className={C(S.detailsColumn, { [S.hasDetails]: showLessonCard })}>
          <div className="flex items-center gap-4">
            {!isAdmin && (
              <Tag
                value={isAdmin ? pathType || contentType : pathType}
                severity="contrast"
                rounded={true}
              />
            )}
            {contentTypeTag && (
              <Tag
                value={contentTypeTag?.replaceAll("_", " ")}
                severity="contrast"
                rounded={true}
              />
            )}
            <h5 className="atlas-h5 white-text"> +{points}</h5>
          </div>
          {isAdmin ? (
            <div className="relative w-full">
              <h3
                className={C(
                  isLargeCard ? `atlas-h1-alt white-text` : S.pathTitle,
                  "transition-all duration-600 ease-in-out group-hover:opacity-0"
                )}
              >
                {name}
              </h3>
              <p
                className={
                  "absolute inset-0 body-sm white-text opacity-0 group-hover:opacity-100 transition-all duration-600 ease-in-out"
                }
              >
                {description.length > 180 ? description.slice(0, 180) + "..." : description}
              </p>
            </div>
          ) : (
            <h3 className={C("atlas-h1-alt white-text", S.pathTitle)}>
              <span className={S.pathTitleInner}>{name}</span>
            </h3>
          )}
          {showDescription && (
            <p className={C("body-md white-text", S.pathDescription)}>{description}</p>
          )}
          {isLargeCard && (
            <Button label={buttonText} onClick={navigateToPath} disabled={disabled} />
          )}
          {(!!lessonsInEnrollment || !!estimatedTimeToComplete) && (
            <div className={S.pathDetails}>
              {!!lessonsInEnrollment && (
                <div className="flex items-center gap-2">
                  <img src={stackSvg} alt="Lesson Stack Icon" />
                  <p className="white-text">{lessonsInEnrollment} lessons</p>
                </div>
              )}
              {!!estimatedTimeToComplete && (
                <div className="flex items-center gap-2">
                  <img src={clockSvg} alt="Clock Icon" />
                  <p className="white-text">{formatDuration(estimatedTimeToComplete)}</p>
                </div>
              )}
            </div>
          )}
          {!isLargeCard && (
            <Button label={buttonText} onClick={navigateToPath} disabled={disabled} />
          )}
        </div>
        {showLessonCard && (
          <div className={S.lessonColumn}>
            <p className="uppercase white-text">Up Next</p>
            <LessonCard
              {...nextLesson}
              contentType={nextLesson.lessonType || "video"}
              disabled={disabled}
            />
          </div>
        )}
        {/* Admin action buttons - only render when props are provided */}
        {isAdmin && (onActivate || onClone) && (
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            {onActivate && (
              <Button
                type="button"
                id="toggle-button"
                severity="contrast"
                text
                icon={activated ? "pi pi-times" : "pi pi-check"}
                tooltip={activated ? "Deactivate" : "Activate"}
                tooltipOptions={{ position: "right" }}
                onClick={onActivate}
                className="z-20"
                loading={isLoadingToggle}
                disabled={isLoadingToggle}
              />
            )}
            {onClone && (
              <Button
                type="button"
                id="clone-button"
                severity="contrast"
                text
                icon="pi pi-copy"
                tooltip="Clone"
                tooltipOptions={{ position: "right" }}
                onClick={onClone}
                className="z-20"
                disabled={disabled}
              />
            )}
          </div>
        )}
        {/* Existing checkbox behavior - only render when showActivated is true and no admin buttons */}
        {showActivated && !isAdmin && (
          <div className="absolute top-2 right-2">
            <Checkbox checked={activated} readOnly />
          </div>
        )}
      </div>
    </div>
  );
};
